import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Trophy, TrendingUp, TrendingDown, Zap } from "lucide-react";

const TYPE_LABEL_ES = {
  stock: "Acciones", crypto: "Cripto", fund: "Fondos",
  real_estate: "Inmueble", deposit: "Depósitos", other: "Otros",
};

/**
 * Ranks every non-matured investment by XIRR. Shows a compact leaderboard
 * with winner/loser tones. Investments without enough cash-flow signal to
 * compute IRR appear at the bottom greyed out.
 */
export default function ReturnsRankingDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/investments/returns")
      .then((r) => setRows(r.data || []))
      .catch((e) => logger.error("returns ranking", e))
      .finally(() => setLoading(false));
  }, [open]);

  const ranked = (rows || []).filter((r) => r.irr != null);
  const unranked = (rows || []).filter((r) => r.irr == null);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl bg-white max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="returns-ranking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#8B6B1F]" />
            Comparar rentabilidades
          </DialogTitle>
          <p className="text-sm text-[#5F7368]">
            Todas tus inversiones ordenadas por IRR (rentabilidad anualizada real, calculada a partir de los movimientos vinculados).
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-[#5F7368]">Calculando…</div>
        ) : ranked.length === 0 && unranked.length === 0 ? (
          <div className="py-8 text-center text-[#8A9992] text-sm">
            Aún no hay inversiones que comparar.
          </div>
        ) : (
          <>
            {/* Podium: best / worst */}
            {ranked.length >= 2 && (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-[#1C7C54]/25 bg-[#1C7C54]/5 p-3">
                  <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#1C7C54] flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> El que más rinde
                  </div>
                  <div className="font-display text-base font-semibold text-[#0A110E] mt-1 truncate">
                    {best.name}
                  </div>
                  <div className="text-sm tabular-nums text-[#1C7C54] font-semibold">
                    +{best.irr.toFixed(2)}% anualizada
                  </div>
                </div>
                <div className="rounded-xl border border-[#D95D39]/25 bg-[#D95D39]/5 p-3">
                  <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#D95D39] flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" /> El que menos rinde
                  </div>
                  <div className="font-display text-base font-semibold text-[#0A110E] mt-1 truncate">
                    {worst.name}
                  </div>
                  <div className={`text-sm tabular-nums font-semibold ${worst.irr >= 0 ? "text-[#8B6B1F]" : "text-[#D95D39]"}`}>
                    {worst.irr >= 0 ? "+" : ""}{worst.irr.toFixed(2)}% anualizada
                  </div>
                </div>
              </div>
            )}

            {/* Ranked list */}
            <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4" data-testid="returns-list">
              <ol className="divide-y divide-[#EBEBEA]">
                {ranked.map((r, i) => (
                  <li
                    key={r.id}
                    data-testid={`ranking-row-${r.id}`}
                    className="py-2.5 flex items-center gap-3"
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      i === 0 ? "bg-[#C89A3F]/20 text-[#8B6B1F]" :
                      i === 1 ? "bg-[#EBEBEA] text-[#5F7368]" :
                      i === 2 ? "bg-[#D6A67A]/20 text-[#8B6B1F]" :
                      "bg-[#FAFAF9] text-[#8A9992]"
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A110E] truncate">{r.name}</div>
                      <div className="text-[11px] text-[#8A9992]">
                        {TYPE_LABEL_ES[r.type] || r.type}
                        {" · "}Invertido {formatCurrency(r.invested, r.currency)}
                        {" · "}Ahora {formatCurrency(r.current_value, r.currency)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-display text-base font-semibold tabular-nums ${
                        r.irr >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"
                      }`} data-testid={`irr-${r.id}`}>
                        {r.irr >= 0 ? "+" : ""}{r.irr.toFixed(2)}%
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#8A9992]">
                        IRR anual
                      </div>
                    </div>
                  </li>
                ))}
              </ol>

              {unranked.length > 0 && (
                <div className="mt-4 rounded-xl border border-dashed border-[#EBEBEA] p-3">
                  <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] flex items-center gap-1 mb-2">
                    <Zap className="w-3 h-3" />
                    Sin rentabilidad calculable ({unranked.length})
                  </div>
                  <div className="text-[11px] text-[#8A9992] mb-2">
                    Vincula movimientos bancarios a estas inversiones para poder calcular su IRR real.
                  </div>
                  <ul className="space-y-1">
                    {unranked.map((r) => (
                      <li key={r.id} className="text-xs text-[#5F7368] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992]" />
                        <span className="flex-1 truncate">{r.name}</span>
                        <span className="tabular-nums text-[#8A9992]">
                          {formatCurrency(r.current_value, r.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
